import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import NotRenderedInExperienceEditor from '../NotRenderedInExperienceEditor';
import TrimOverview from './TrimOverview';
import getTrimWithInteriorColorPricing from '../../utils/getTrimWithInteriorColorPricing';
import getWebCatalogTransmission from './service/getWebCatalogTransmission';
import safelyStringifyJSON from '../../utils/safelyStringifyJSON';
import useFetchCurrentVehiclePaymentByDealerProvince from '../TrimPayment/InventoryTrimPayment/useFetchCurrentVehiclePaymentByDealerProvince';
import useFetchLayoutServiceAssets from '../../core/apiHooks/useFetchLayoutServiceAssets';
import useFetchLayoutServiceTransmission from '../../core/apiHooks/useFetchLayoutServiceTransmission';
import useGetTrimAssetsProps from './hooks/useGetTrimAssetsProps';
import useModelData from '../TrimCardGrid/hooks/useModelData';
import usePageTitle from '../../utils/hooks/usePageTitle';
import useProductFinancial from '../../apiHooks/useProductFinancial';
import { ACURA_SITE_NAME, HONDA_SITE_NAME } from '../../utils/constants';
import { INVENTORY_CALCULATOR } from '../TrimPayment/constants';
import { INVENTORY_VARIANT } from './service/constants';
import { assetType, removeConfigureBapToastNotification, setBackStepUrl, setInteriorColorIsInvalid, setModelName, setNameBadge, setSelectedAssetType, setTrimName, } from '../../core/reducers/ui';
import { createObjectFromProps, isEmpty } from '../../utils/object';
import { persistCurrentVehicleToUrl, selectCurrentVehicle, setColors } from '../../core/reducers/currentVehicle';
import { selectFlattenedCurrentVehicleFromVehicles } from '../../core/reducers/vehicles';
import { isItemIncludedForBAP, useAppDispatch, useAppSelector } from '../../core/utils';
import { useAppName, usePageState, useUserOrDefaultProvince } from '../../utils/sitecoreContext';
import useBapModelPageRoute from '../../utils/hooks/bap/useBapModelPageRoute';
const TrimOverviewMap = ({ variant: propVariant, fields, rendering, params }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const appName = useAppName();
    const dispatch = useAppDispatch();
    const pageState = usePageState();
    let variant;
    let mappedFields = ((_a = fields === null || fields === void 0 ? void 0 : fields.data) === null || _a === void 0 ? void 0 : _a.value) || {};
    switch (appName) {
        case ACURA_SITE_NAME:
            break;
        case HONDA_SITE_NAME:
        default:
    }
    const isInventoryVariant = (params === null || params === void 0 ? void 0 : params.variant) === INVENTORY_VARIANT;
    const appNameConfig = useMemo(() => ({ variant: propVariant !== null && propVariant !== void 0 ? propVariant : variant }), [propVariant, variant]);
    mappedFields = useMemo(() => createObjectFromProps(mappedFields), [safelyStringifyJSON(mappedFields)]);
    const provinceCode = useUserOrDefaultProvince();
    const { modelKey, modelYear = '', trimKey, transmissionKey } = useSelector(selectCurrentVehicle);
    const models = (_b = mappedFields === null || mappedFields === void 0 ? void 0 : mappedFields.catalog) === null || _b === void 0 ? void 0 : _b.models;
    const model = (_d = (_c = models === null || models === void 0 ? void 0 : models[0]) === null || _c === void 0 ? void 0 : _c.items) === null || _d === void 0 ? void 0 : _d.find((item) => (item === null || item === void 0 ? void 0 : item.detKey) === modelKey);
    const sitecoreId = model === null || model === void 0 ? void 0 : model.id;
    const backStepUrl = ((_h = (_g = (_f = (_e = fields === null || fields === void 0 ? void 0 : fields.data) === null || _e === void 0 ? void 0 : _e.value) === null || _f === void 0 ? void 0 : _f.backStepBaseUrl) === null || _g === void 0 ? void 0 : _g.item) === null || _h === void 0 ? void 0 : _h.value) || {};
    const { nameBadge, trims, isFetching: isFetchingTrims } = useModelData(sitecoreId, modelYear);
    const transmission = getWebCatalogTransmission(modelKey || '', modelYear || '', trimKey || '', transmissionKey || '', models);
    const transmissionId = transmission === null || transmission === void 0 ? void 0 : transmission.id;
    const { data: transmissionData, isFetching: isFetchingTransmissions } = useFetchLayoutServiceTransmission(transmissionId);
    const transmissionName = transmissionData === null || transmissionData === void 0 ? void 0 : transmissionData.name;
    const trimName = transmissionData === null || transmissionData === void 0 ? void 0 : transmissionData.trimName;
    const modelName = transmissionData === null || transmissionData === void 0 ? void 0 : transmissionData.modelName;
    const { isFetching: isFetchingAssets } = useFetchLayoutServiceAssets({
        transmissionId,
    });
    const vehicle = useAppSelector(selectFlattenedCurrentVehicleFromVehicles);
    const pageTitle = usePageTitle(modelName, isInventoryVariant);
    useEffect(() => {
        if ((vehicle === null || vehicle === void 0 ? void 0 : vehicle.exteriorColourKey) && (vehicle === null || vehicle === void 0 ? void 0 : vehicle.interiorColourKey)) {
            dispatch(setColors({
                exteriorColor: vehicle.exteriorColourKey,
                interiorColor: vehicle.interiorColourKey,
            }));
            dispatch(persistCurrentVehicleToUrl());
        }
    }, [vehicle === null || vehicle === void 0 ? void 0 : vehicle.exteriorColourKey, vehicle === null || vehicle === void 0 ? void 0 : vehicle.interiorColourKey, dispatch, vehicle]);
    useEffect(() => {
        dispatch(setBackStepUrl(backStepUrl));
        dispatch(setTrimName(trimName || ''));
        dispatch(setModelName(modelName || ''));
        if (nameBadge) {
            dispatch(setNameBadge(nameBadge));
        }
        return () => {
            // reset state on dismount
            dispatch(setSelectedAssetType(assetType.exterior));
            dispatch(setTrimName(''));
            dispatch(setModelName(''));
        };
    }, [backStepUrl, dispatch, trimName, modelName, nameBadge]);
    const removeToastNotificationHandler = (id) => {
        dispatch(removeConfigureBapToastNotification(id));
    };
    const { exteriorColorKey, interiorColorKey } = useAppSelector(state => state.currentVehicle);
    const { toastNotifications } = useAppSelector(state => state.ui.ConfigureBap);
    const trimAssetsProps = useGetTrimAssetsProps(rendering === null || rendering === void 0 ? void 0 : rendering.componentName, transmissionId, isInventoryVariant);
    const inventoryPayment = useFetchCurrentVehiclePaymentByDealerProvince(INVENTORY_CALCULATOR, isInventoryVariant);
    const { error: inventoryPaymentError } = inventoryPayment;
    const { financial, setModels, hasError, isFetching: isFinancialsFetching, } = useProductFinancial({ vehicleType: undefined });
    useEffect(() => {
        const financialModels = [
            {
                modelYear: vehicle.year,
                modelKey: vehicle.key,
            },
        ];
        if (models.length) {
            setModels(financialModels);
        }
    }, [vehicle.year, vehicle.key, setModels]);
    const trim = {
        modelKey: vehicle.key || '',
        trimKey: vehicle.trimKey || '',
        defaultTransmission: (vehicle === null || vehicle === void 0 ? void 0 : vehicle.transmissionKey) || '',
        defaultExteriorColorKey: vehicle.defaultExteriorColourKey,
        defaultInteriorColorKey: vehicle.defaultInteriorColourKey,
    };
    const hasInvalidInteriorPricing = !getTrimWithInteriorColorPricing(financial, trim, hasError, vehicle === null || vehicle === void 0 ? void 0 : vehicle.exteriorColourKey, vehicle.transmissionKey, vehicle === null || vehicle === void 0 ? void 0 : vehicle.interiorColourKey);
    useEffect(() => {
        const financialsIsEmpty = isEmpty(financial);
        if (financialsIsEmpty || (!isFinancialsFetching && !hasInvalidInteriorPricing)) {
            dispatch(setInteriorColorIsInvalid(false));
        }
        if (!isFinancialsFetching && !financialsIsEmpty && hasInvalidInteriorPricing) {
            dispatch(setInteriorColorIsInvalid(true));
        }
    }, [isFinancialsFetching, hasInvalidInteriorPricing, financial, dispatch]);
    const { isBapPage, bapModelURL } = useBapModelPageRoute();
    if (!mappedFields || isEmpty(mappedFields) || !mappedFields) {
        return null;
    }
    const isTrimsEmpty = !isFetchingTrims && !trims.length;
    const isTransmissionLoaded = !isFetchingTransmissions && transmissionData;
    if (isTrimsEmpty || (isBapPage && isTransmissionLoaded && !isItemIncludedForBAP(transmissionData, provinceCode))) {
        return _jsx(Redirect, { to: bapModelURL });
    }
    if (pageState === 'edit') {
        return _jsx(NotRenderedInExperienceEditor, { componentName: rendering === null || rendering === void 0 ? void 0 : rendering.componentName });
    }
    return (_jsx(TrimOverview, { appNameConfig: appNameConfig, componentName: rendering === null || rendering === void 0 ? void 0 : rendering.componentName, trim: {
            modelKey,
            modelYear,
            modelName,
            trimName,
            transmissionKey,
            transmissionName,
            transmissionId,
            exteriorColorKey,
            interiorColorKey,
        }, isFetchingAssets: isFetchingAssets, removeToastNotificationHandler: removeToastNotificationHandler, nameBadge: nameBadge, stickToComponentDesktop: (_j = mappedFields === null || mappedFields === void 0 ? void 0 : mappedFields.stickToComponentDesktop) === null || _j === void 0 ? void 0 : _j.name, stickToComponentMobile: (_k = mappedFields === null || mappedFields === void 0 ? void 0 : mappedFields.stickToComponentMobile) === null || _k === void 0 ? void 0 : _k.name, showPrice: isInventoryVariant, showPriceError: !!inventoryPaymentError, showSaveBuild: !isInventoryVariant, toastNotifications: toastNotifications, trimAssetsProps: trimAssetsProps, isInventoryVariant: isInventoryVariant, paymentData: inventoryPayment, pageTitle: pageTitle }));
};
export default TrimOverviewMap;
